import {
  Application,
  Container,
  Graphics,
  Sprite,
  Text,
  type FederatedPointerEvent,
  type Spritesheet,
} from "pixi.js";
import palette from "../ui/theme/palette.json";
import { loadBoardArt, loadArtWithFallback, loadUnitArt, withDetails } from "./art.js";
import { facingFor, worksiteMotion } from "./motion.js";
import { gridToScreen, type Point } from "./iso.js";
import { loadWorldMaterial } from "./worldLandscape.js";
import { villageGround } from "./villageGround.js";
import { LandscapeBackdrop } from "./landscapeBackdrop.js";
import { playSound } from "../lib/sound.js";
import { workerAction } from "./actionMotion.js";
import { VillageLife } from "./villageLife.js";
import { buildingFrame } from "./presentation.js";
import { managePresentation, renderResolution } from "../lib/graphics.js";
import {
  CHIMNEYS,
  DECOR,
  PLOTS,
  ROAD,
  WINDMILL,
  cellsOf,
  CENTRE,
  forestCells,
  villageCells,
  type Plot,
} from "../village/layout.js";

/**
 * The village, drawn (village screen, Sep 2026).
 *
 * The home screen used to be a list of seventeen rows. It is now the village
 * itself: every building on its own plot, a road from the gate to the Main
 * Building, and enough going on — sails turning, chimney smoke, people on the
 * road — that it reads as a place rather than a spreadsheet.
 *
 * The view knows nothing about the economy. It is told, through `setState`,
 * which buildings stand at what level, which are being built, which could be
 * built now, and which one the next-step card is pointing at; it reports taps
 * by building id. Everything about what a tap MEANS lives in React.
 *
 * D-121 artwork comes from tools/art/ancient, with the established Kenney
 * terrain and the earlier village atlas retained as a loading fallback.
 * Queued builds and level changes drive construction; ambient motion never
 * writes to the economy. Without artwork, badges and plot taps still work.
 */

export interface Construction {
  readonly id: string;
  /** 0..1 */
  readonly progress: number;
  /** What the bubble says, e.g. "2:41". */
  readonly label: string;
}

export interface VillageState {
  /** Building id → level. Absent or 0 = not built. */
  readonly levels: Readonly<Record<string, number>>;
  /** Buildings in the build queue. */
  readonly building: readonly Construction[];
  /** Level-0 buildings whose prerequisites are met: their plots invite a tap. */
  readonly buildable: ReadonlySet<string>;
  /** Units are in training (a figure drills at the Barracks). */
  readonly training: boolean;
  /** The building the next-step card points at, or null. */
  readonly hint: string | null;
}

const EMPTY_STATE: VillageState = {
  levels: {},
  building: [],
  buildable: new Set(),
  training: false,
  hint: null,
};

/** One storey of a building, in world pixels on screen. */
const STOREY = 39;
/** The windmill's sails: frames per second, and how many frames there are. */
const SAIL_FPS = 8;
const SAIL_FRAMES = 6;
const MAX_SCALE = 3;
const TAP_SLOP = 8;

/** A stable pseudo-random number in [0, 1) for a cell: the same forest every time. */
function cellHash(x: number, y: number): number {
  let h = (Math.imul(x, 374761393) + Math.imul(y, 668265263)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function hex(css: string): number {
  return Number.parseInt(css.replace("#", ""), 16);
}

let villageArt: Promise<Spritesheet | null> | null = null;
export function loadVillageArt(): Promise<Spritesheet | null> {
  villageArt ??= loadArtWithFallback("village", "ancient-village").then((sheet) =>
    withDetails(sheet, "ancient-buildings").then((base) => withDetails(base, "ancient-progression"))
  );
  return villageArt;
}

/** What the tag under each building says. Short: seventeen of them share a phone screen. */
const SHORT_NAME: Record<string, string> = {
  main_building: "Main Building",
  woodcutter: "Woodcutter",
  clay_pit: "Clay Pit",
  iron_mine: "Iron Mine",
  grain_farm: "Farm",
  warehouse: "Warehouse",
  granary: "Granary",
  marketplace: "Market",
  residence: "Residence",
  embassy: "Embassy",
  barracks: "Barracks",
  stable: "Stable",
  workshop: "Workshop",
  shrine: "Shrine",
  rally_point: "Rally Point",
  engineers_guild: "Guild",
  wall: "Wall",
};

const depthOf = (plot: Plot): number => plot.x + plot.w - 1 + plot.y + plot.d - 1;
const centreOf = (plot: Plot): Point =>
  gridToScreen(plot.x + (plot.w - 1) / 2, plot.y + (plot.d - 1) / 2);

export class VillageView {
  private releasePresentation: (() => void) | null = null;
  readonly app: Application;
  private readonly world = new Container();
  private readonly ground = new Container();
  private meadow: Sprite | null = null;
  private backdrop: LandscapeBackdrop | null = null;
  private selected: string | null = null;
  private labels = false;
  private readonly selection = new Graphics();
  private readonly objects = new Container();
  private readonly fx = new Graphics();
  private readonly marks = new Graphics();
  private readonly overlay = new Container();
  private readonly hintArrow = new Graphics();

  private readonly board: Spritesheet | null;
  private readonly village: Spritesheet | null;
  private readonly plotSprites = new Map<string, Container>();
  private readonly buildingLife = new Map<string, VillageLife>();
  /** Each building's tag, so a tap on the tag opens the building too. */
  private readonly tags = new Map<string, Container>();
  private sails: Sprite | null = null;
  private readonly people: { g: Graphics; offset: number; speed: number; tint: number }[] = [];
  private readonly walkers = new Map<Graphics, Sprite>();
  private readonly constructionArt = new Map<string, Container>();
  private readonly constructionReveals = new Map<
    string,
    {
      building: Sprite | null;
      mask: Graphics | null;
      scaffold: Sprite | null;
      shown: number;
      target: number;
    }
  >();
  private readonly builders = new Map<string, Sprite>();
  private readonly celebrations = new Map<string, number>();
  private readonly reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  private unitArt: Spritesheet | null = null;
  private settled = false;
  private recruit: Sprite | null = null;
  private lifeTime = 0;

  private state: VillageState = EMPTY_STATE;
  private onTapFn: ((id: string) => void) | null = null;
  private destroyed = false;

  private readonly pointers = new Map<number, Point>();
  private pressedAt: Point | null = null;
  private dragged = false;
  private gesture: { distance: number; midpoint: Point } | null = null;

  private constructor(app: Application, board: Spritesheet | null, village: Spritesheet | null) {
    this.app = app;
    this.board = board;
    this.village = village;
    this.ground.sortableChildren = true;
    this.objects.sortableChildren = true;
    this.world.addChild(
      this.ground,
      this.selection,
      this.objects,
      this.fx,
      this.marks,
      this.overlay,
      this.hintArrow
    );
    app.stage.addChild(this.world);

    this.drawGround();
    this.spawnPeople();
    this.redraw();
    this.bindPointer();
    app.ticker.add(() => this.animate());
  }

  static async mount(host: HTMLElement): Promise<VillageView> {
    const app = new Application();
    await app.init({
      background: palette.bg,
      antialias: true,
      resizeTo: host,
      resolution: renderResolution(),
      autoDensity: true,
      preference: "webgl",
    });
    host.appendChild(app.canvas);
    const [board, village, units, material] = await Promise.all([
      loadBoardArt(),
      loadVillageArt(),
      loadUnitArt(),
      loadWorldMaterial(),
    ]);
    const view = new VillageView(app, board, village);
    view.unitArt = units;
    if (material) {
      view.backdrop = new LandscapeBackdrop(material);
      app.stage.addChildAt(view.backdrop.sprite, 0);
      for (const child of view.ground.removeChildren()) child.destroy();
      view.meadow = villageGround(material);
      view.ground.addChild(view.meadow);
    }
    view.frame();
    app.renderer.on("resize", () => view.frame());
    view.releasePresentation = managePresentation(app);
    return view;
  }

  onTap(fn: (buildingId: string) => void): void {
    this.onTapFn = fn;
  }

  setPresentation(selected: string | null, labels: boolean): void {
    if (this.destroyed) return;
    this.selected = selected;
    this.labels = labels;
    this.selection.clear();
    const plot = PLOTS.find((p) => p.id === selected);
    if (plot) {
      const at = centreOf(plot);
      this.selection
        .ellipse(at.x, at.y + 4, (plot.w + plot.d) * 15, (plot.w + plot.d) * 7.5)
        .fill({ color: 0xffda81, alpha: 0.18 })
        .stroke({ color: 0xffdf91, width: 2.5 });
    }
    this.drawMarks();
  }

  zoomBy(factor: number): void {
    this.zoomAround({ x: this.app.screen.width / 2, y: this.app.screen.height / 2 }, factor);
  }

  /**
   * Called every second or so by React. Only a change in what STANDS (levels)
   * rebuilds the scene; timers, the hint and "can build" just redraw the marks.
   */
  setState(state: VillageState): void {
    if (this.destroyed) return;
    const structural = (s: VillageState): string =>
      PLOTS.map((p) => `${p.id}:${s.levels[p.id] ?? 0}`).join(",");
    const rebuild = structural(state) !== structural(this.state);
    if (this.settled) {
      for (const job of this.state.building) {
        if ((state.levels[job.id] ?? 0) > (this.state.levels[job.id] ?? 0)) {
          this.celebrations.set(job.id, this.app.ticker.lastTime / 1000);
          playSound("complete");
        }
      }
    }
    this.settled = Object.keys(state.levels).length > 0;
    this.state = state;
    if (rebuild) this.redraw();
    else this.drawMarks();
  }

  /** Where a building stands on the page (CSS px), for tests and tooling. */
  screenOfBuilding(id: string): Point | null {
    const plot = PLOTS.find((p) => p.id === id);
    if (!plot) return null;
    const g = this.world.toGlobal(centreOf(plot));
    const rect = this.app.canvas.getBoundingClientRect();
    return { x: rect.left + g.x, y: rect.top + g.y };
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.releasePresentation?.();
    this.app.ticker?.stop();
    this.meadow?.texture.destroy(true);
    this.backdrop?.destroy();
    // `{ removeView: true }`, NOT `true`: in Pixi 8, `true` also releases the
    // GLOBAL texture pool every canvas on the page shares. Closing the village
    // behind the tutorial then broke the tutorial board mid-step — it went dark
    // at the gate (friends test #2 prep, Sep 2026).
    this.app.destroy({ removeView: true }, { children: true });
  }

  // --- drawing -------------------------------------------------------------

  private drawGround(): void {
    // The forest floor around the clearing: the same grass, in shade.
    const grass = this.board?.textures.grass;
    for (const [x, y] of forestCells()) {
      const at = gridToScreen(x, y);
      if (grass) {
        const sprite = new Sprite(grass);
        sprite.anchor.set(grass.defaultAnchor?.x ?? 0.5, grass.defaultAnchor?.y ?? 0.5);
        sprite.position.set(at.x, at.y);
        sprite.tint = 0x98a780;
        sprite.zIndex = x + y - 100;
        this.ground.addChild(sprite);
      } else {
        const g = new Graphics();
        g.poly([at.x, at.y - 16, at.x + 32, at.y, at.x, at.y + 16, at.x - 32, at.y]);
        g.fill({ color: 0x2f6f55 });
        this.ground.addChild(g);
      }
    }
    const road = new Set(ROAD.map(([x, y]) => `${x},${y}`));
    const [gx, gy] = ROAD[0]!;
    for (const [x, y] of villageCells()) {
      const name = road.has(`${x},${y}`)
        ? this.roadPiece(road, x, y, x === gx && y === gy)
        : "grass";
      const texture = this.board?.textures[name] ?? this.board?.textures.grass;
      if (texture) {
        const sprite = new Sprite(texture);
        sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 0.5);
        const at = gridToScreen(x, y);
        sprite.position.set(at.x, at.y);
        sprite.tint = name === "grass" ? 0xded2ab : 0xf3dfb3;
        sprite.zIndex = x + y;
        this.ground.addChild(sprite);
      } else {
        const g = new Graphics();
        const c = gridToScreen(x, y);
        g.poly([c.x, c.y - 16, c.x + 32, c.y, c.x, c.y + 16, c.x - 32, c.y]);
        g.fill({ color: road.has(`${x},${y}`) ? hex(palette.road) : 0x3f9d7a });
        g.stroke({ color: hex(palette.grid), width: 1, alpha: 0.4 });
        this.ground.addChild(g);
      }
    }
  }

  /** The same edge naming as the battle board's road (pixi/road.ts). */
  private roadPiece(road: Set<string>, x: number, y: number, isGate: boolean): string {
    const steps: [string, number, number][] = [
      ["NE", 0, -1],
      ["SE", 1, 0],
      ["SW", 0, 1],
      ["NW", -1, 0],
    ];
    const main = PLOTS.find((p) => p.id === "main_building")!;
    const mainCells = new Set(cellsOf(main).map(([a, b]) => `${a},${b}`));
    const open = steps
      .filter(
        ([, dx, dy]) => road.has(`${x + dx},${y + dy}`) || mainCells.has(`${x + dx},${y + dy}`)
      )
      .map(([e]) => e);
    if (isGate && open.length === 1) return `gate_${open[0]}`;
    if (open.length === 2) return `road_${open.join("_")}`;
    if (open.length === 1) return `road_${open[0]}`;
    return "dirt";
  }

  private picture(frame: string, at: Point, zIndex: number): Container {
    const holder = new Container();
    const texture =
      this.village?.textures[frame] ?? this.village?.textures[frame.replace(/_tier[23]$/, "")];
    if (texture) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
      sprite.position.set(at.x, at.y);
      holder.addChild(sprite);
    }
    holder.zIndex = zIndex;
    return holder;
  }

  private redraw(): void {
    for (const child of this.objects.removeChildren()) {
      if (!this.people.some((p) => p.g === child)) child.destroy({ children: true });
    }
    this.plotSprites.clear();
    this.buildingLife.clear();
    this.constructionArt.clear();
    this.constructionReveals.clear();
    this.builders.clear();
    this.sails = null;
    this.recruit = null;

    for (const plot of PLOTS) {
      const level = this.state.levels[plot.id] ?? 0;
      const holder = this.picture(
        buildingFrame(plot.id, level),
        centreOf(plot),
        depthOf(plot) + 0.5
      );
      this.objects.addChild(holder);
      this.plotSprites.set(plot.id, holder);
      if (level > 0 && this.village?.textures.construction_foundation) {
        const life = new VillageLife(plot.id, this.buildingLife.size);
        life.position.copyFrom(centreOf(plot));
        life.zIndex = depthOf(plot) + 0.75;
        this.objects.addChild(life);
        this.buildingLife.set(plot.id, life);
      }
    }
    for (const d of DECOR) {
      this.objects.addChild(this.picture(d.frame, gridToScreen(d.x, d.y), d.x + d.y + 0.5));
    }
    // The forest around the clearing. Tall trees behind the village, only low
    // ones in front of it, so nothing hides the gate or the buildings.
    const tall = ["decor_treeHigh", "decor_treeHighRound", "decor_tree", "decor_treeCrooked"];
    // Quiet outlying groves fill wide displays without covering any playable plot.
    for (let i = 0; i < 48; i++) {
      const side = i % 2 ? 1 : -1;
      const grove = Math.floor(i / 8);
      const x = side * (460 + (grove % 3) * 220 + cellHash(i, 41) * 120);
      const y = 40 + Math.floor(grove / 2) * 235 + cellHash(i, 83) * 115;
      const decor = this.picture(tall[i % tall.length]!, { x, y }, y / 16);
      this.objects.addChild(decor);
    }
    const [gx, gy] = ROAD[0]!;
    for (const [x, y] of forestCells()) {
      // Leave the way out of the gate open.
      if (Math.abs(x - gx) <= 2 && Math.abs(y - gy) <= 2) continue;
      const roll = cellHash(x, y);
      const front = x + y > 2 * CENTRE + 4;
      if (roll > (front ? 0.3 : 0.65)) continue;
      const frame = front
        ? roll < 0.1
          ? "decor_rockSmall"
          : "decor_tree"
        : tall[Math.floor(cellHash(y, x) * tall.length)]!;
      this.objects.addChild(this.picture(frame, gridToScreen(x, y), x + y + 0.5));
    }
    const farm = PLOTS.find((p) => p.id === WINDMILL.plot)!;
    const first = this.village?.textures.blades_0;
    if (
      !this.village?.textures.construction_foundation &&
      (this.state.levels[farm.id] ?? 0) > 0 &&
      first
    ) {
      const sails = new Sprite(first);
      sails.anchor.set(first.defaultAnchor?.x ?? 0.5, first.defaultAnchor?.y ?? 1);
      const at = gridToScreen(farm.x, farm.y);
      sails.position.set(at.x, at.y);
      sails.zIndex = depthOf(farm) + 0.6;
      this.objects.addChild(sails);
      this.sails = sails;
    }
    for (const p of this.people) this.objects.addChild(p.g);

    this.drawMarks();
  }

  /**
   * A tag under every building — its name and level, "+ name" where you can
   * build now, a padlock where you cannot yet — and scaffolding plus a timer
   * over anything being built.
   *
   * Tags and timers are drawn at one size on SCREEN, whatever the zoom: the
   * whole village fits a phone at about half size, and text at half size is
   * unreadable. Scaffolding stays in the world, because it is part of the
   * building.
   */
  private drawMarks(): void {
    const g = this.marks;
    g.clear();
    for (const child of this.overlay.removeChildren()) child.destroy({ children: true });
    this.tags.clear();
    // Timer updates keep the same scaffold, mask and worker. Only removed
    // jobs are destroyed, so a builder cannot blink back to the origin.
    for (const [id, holder] of this.constructionArt) {
      if (this.state.building.some((job) => job.id === id)) continue;
      holder.destroy({ children: true });
      this.constructionArt.delete(id);
      this.constructionReveals.delete(id);
      this.builders.delete(id);
    }

    for (const plot of PLOTS) {
      const level = this.state.levels[plot.id] ?? 0;
      const c = centreOf(plot);
      // The front tip of the plot: the tag hangs there, below the picture.
      const at = { x: c.x, y: c.y + ((plot.w + plot.d) / 2) * 16 - 4 };
      const job = this.state.building.find((b) => b.id === plot.id);
      const name = SHORT_NAME[plot.id] ?? plot.id;

      if (job) {
        this.drawConstruction(plot, job);
        this.tag(plot.id, at, name, level > 0 ? String(level) : null, "busy");
        continue;
      }
      if (level > 0) this.tag(plot.id, at, name, String(level), "built");
      else if (this.state.buildable.has(plot.id)) this.tag(plot.id, at, name, "+", "buildable");
      else this.tag(plot.id, at, name, null, "locked");
    }
    this.fitMarks();
  }

  /** One screen-sized tag, pinned to a world point. */
  private tag(
    id: string,
    at: Point,
    name: string,
    badge: string | null,
    kind: "built" | "buildable" | "locked" | "busy"
  ): void {
    const holder = new Container();
    holder.position.set(at.x, at.y);
    const dim = kind === "locked";
    const expanded =
      this.labels || this.selected === id || this.state.hint === id || kind === "busy";
    const text = new Text({
      text: expanded ? name : "",
      style: {
        fontFamily: "system-ui, sans-serif",
        fontSize: 11,
        fontWeight: "700",
        fill: hex(dim ? palette["ink-dim"] : palette.ink),
      },
    });
    const badgeW = badge ? 18 : dim ? 12 : 0;
    const padX = 6;
    const width = (expanded ? text.width + 3 : 0) + padX * 2 + badgeW;
    const height = 22;
    const bg = new Graphics();
    bg.roundRect(-width / 2, 0, width, height, 5);
    bg.fill({ color: 0x213d36, alpha: dim ? 0.65 : 0.93 });
    const hinted = this.state.hint === id;
    bg.stroke({
      color: hex(
        hinted || kind === "buildable" || kind === "busy" ? palette.gold : palette["panel-edge"]
      ),
      width: hinted ? 3 : kind === "buildable" ? 2 : 1,
    });
    const left = -width / 2 + padX;
    if (badge) {
      bg.circle(left + 8, height / 2, 8);
      bg.fill({ color: hex(palette.gold) });
      const b = new Text({
        text: badge,
        style: {
          fontFamily: "system-ui, sans-serif",
          fontSize: badge === "+" ? 13 : 10,
          fontWeight: "800",
          fill: hex(palette.bg),
        },
      });
      b.anchor.set(0.5);
      b.position.set(left + 8, height / 2 + (badge === "+" ? -0.5 : 0));
      holder.addChild(bg, b);
    } else if (dim) {
      // A padlock: shackle, then body.
      const x = left + 5;
      const y = height / 2;
      bg.moveTo(x - 3, y - 1);
      bg.arc(x, y - 1, 3, Math.PI, 0);
      bg.stroke({ color: hex(palette["ink-dim"]), width: 1.6 });
      bg.roundRect(x - 4.5, y - 1, 9, 6.5, 1.5);
      bg.fill({ color: hex(palette["ink-dim"]) });
      holder.addChild(bg);
    } else {
      holder.addChild(bg);
    }
    text.anchor.set(0, 0.5);
    text.position.set(left + (badgeW ? badgeW + 3 : 0), height / 2);
    holder.addChild(text);
    this.overlay.addChild(holder);
    this.tags.set(id, holder);
  }

  /** Tags and timers keep their screen size at any zoom. */
  private fitMarks(): void {
    const k = 1 / this.world.scale.x;
    for (const child of this.overlay.children) child.scale.set(k);
  }

  private drawConstruction(plot: Plot, job: Construction): void {
    const g = this.marks;
    const c = centreOf(plot);
    const height = (plot.w + plot.d) * 12 + STOREY;
    const authored = this.village?.textures.construction_scaffold;
    if (authored) this.drawConstructionArt(plot, job);
    // The procedural scaffold remains available when artwork cannot load.
    const half = ((plot.w + plot.d) / 2) * 16;
    const xs = [c.x - half, c.x, c.x + half];
    for (const x of xs) {
      g.moveTo(x, c.y + 4);
      g.lineTo(x, c.y - height);
    }
    for (let k = 1; k <= 3; k++) {
      const y = c.y - (height * k) / 3.4;
      g.moveTo(xs[0]!, y + 6);
      g.lineTo(xs[2]!, y - 6);
    }
    g.stroke({ color: 0xc08a52, width: 2.2, alpha: authored ? 0 : 0.95 });

    // A bubble with the time left and a progress bar, over the building.
    const bubble = new Container();
    bubble.position.set(c.x, c.y - height - 6);
    const b = new Graphics();
    b.roundRect(-34, -30, 68, 26, 9);
    b.fill({ color: hex(palette["bg-raised"]), alpha: 0.95 });
    b.stroke({ color: hex(palette.gold), width: 2 });
    b.roundRect(-26, -11, 52, 3, 1.5);
    b.fill({ color: hex(palette["panel-edge"]) });
    b.roundRect(-26, -11, 52 * Math.max(0, Math.min(1, job.progress)), 3, 1.5);
    b.fill({ color: hex(palette.gold) });
    const t = new Text({
      text: job.label,
      style: {
        fontFamily: "system-ui, sans-serif",
        fontSize: 11,
        fontWeight: "800",
        fill: hex(palette.gold),
      },
    });
    t.anchor.set(0.5, 1);
    t.position.set(0, -12);
    bubble.addChild(b, t);
    this.overlay.addChild(bubble);
  }

  // --- life ------------------------------------------------------------------

  private drawConstructionArt(plot: Plot, job: Construction): void {
    const progress = Math.max(0, Math.min(1, job.progress));
    const existing = this.constructionReveals.get(plot.id);
    if (existing) {
      existing.target = progress;
      return;
    }
    const c = centreOf(plot);
    const holder = new Container();
    holder.zIndex = depthOf(plot) + 0.65;
    const put = (name: string, at: Point, scale = 1): Sprite | null => {
      const texture = this.village?.textures[name];
      if (!texture) return null;
      const sprite = new Sprite(texture);
      sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
      sprite.position.set(at.x, at.y);
      sprite.scale.set(scale);
      holder.addChild(sprite);
      return sprite;
    };
    const scale = ((plot.w + plot.d) * 24) / 88;
    let building: Sprite | null = null;
    let mask: Graphics | null = null;
    if (!(this.state.levels[plot.id] ?? 0)) {
      put("construction_foundation", c, scale);
      building = put(plot.id, c);
      if (building) {
        // Reveal the actual building from its foundations using the real queue progress.
        mask = new Graphics();
        const bottom = c.y + (1 - building.anchor.y) * building.height;
        mask.rect(
          c.x - building.width / 2,
          bottom - building.height * progress,
          building.width,
          building.height * progress
        );
        mask.fill({ color: 0xffffff });
        holder.addChild(mask);
        building.mask = mask;
      }
    }
    const scaffold = put("construction_scaffold", c, scale);
    if (scaffold) scaffold.alpha = 0.85 - progress * 0.35;
    put("construction_crane", { x: c.x + (plot.w + plot.d) * 14, y: c.y + 6 }, 0.65);
    const worker = this.unitArt?.textures.worker_SW_0;
    if (worker) {
      const sprite = new Sprite(worker);
      sprite.anchor.set(worker.defaultAnchor?.x ?? 0.5, worker.defaultAnchor?.y ?? 1);
      holder.addChild(sprite);
      this.builders.set(plot.id, sprite);
    }
    this.objects.addChild(holder);
    this.constructionArt.set(plot.id, holder);
    this.constructionReveals.set(plot.id, {
      building,
      mask,
      scaffold,
      shown: progress,
      target: progress,
    });
  }

  private spawnPeople(): void {
    const tints = [0xb1462f, 0x3f7fb5, 0xd9c26a];
    tints.forEach((tint, i) => {
      this.people.push({ g: new Graphics(), offset: i / 3, speed: 0.018 + i * 0.004, tint });
    });
  }

  private animate(): void {
    if (this.destroyed) return;
    const now = this.app.ticker.lastTime / 1000;
    // Resume where the village was, instead of jumping after a hidden tab.
    if (!this.reducedMotion.matches && !document.hidden) {
      this.lifeTime += Math.min(this.app.ticker.deltaMS, 50) / 1000;
    }
    const t = this.lifeTime;
    for (const [id, life] of this.buildingLife) {
      life.update(
        t,
        this.unitArt,
        this.reducedMotion.matches,
        this.state.building.some((job) => job.id === id)
      );
    }

    for (const reveal of this.constructionReveals.values()) {
      if (reveal.shown === reveal.target) continue;
      const blend = this.reducedMotion.matches ? 1 : 1 - Math.exp(-this.app.ticker.deltaMS / 140);
      reveal.shown += (reveal.target - reveal.shown) * blend;
      if (Math.abs(reveal.target - reveal.shown) < 0.0001) reveal.shown = reveal.target;
      const { building, mask, scaffold, shown } = reveal;
      if (building && mask) {
        const bottom = building.y + (1 - building.anchor.y) * building.height;
        mask
          .clear()
          .rect(
            building.x - building.width * building.anchor.x,
            bottom - building.height * shown,
            building.width,
            building.height * shown
          )
          .fill({ color: 0xffffff });
      }
      if (scaffold) scaffold.alpha = 0.85 - shown * 0.35;
    }

    if (this.sails && this.village) {
      const frame = Math.floor(t * SAIL_FPS) % SAIL_FRAMES;
      const texture = this.village.textures[`blades_${frame}`];
      if (texture && this.sails.texture !== texture) this.sails.texture = texture;
    }

    // Smoke from the chimneys of buildings that stand.
    const fx = this.fx;
    fx.clear();
    const chimneys = this.village?.textures.construction_foundation
      ? [
          { plot: "clay_pit", x: 7, y: 2, lift: 54 },
          { plot: "workshop", x: 13, y: 18, lift: 49 },
          { plot: "residence", x: 13, y: 8, lift: 54 },
        ]
      : CHIMNEYS;
    for (const chimney of chimneys) {
      if ((this.state.levels[chimney.plot] ?? 0) <= 0) continue;
      const base = gridToScreen(chimney.x, chimney.y);
      for (let k = 0; k < 4; k++) {
        const phase = (t * 0.35 + k / 4) % 1;
        fx.circle(
          base.x - 8 + Math.sin((phase + k) * 4) * 3 + phase * 6,
          base.y - chimney.lift - phase * 34,
          3 + phase * 6
        );
        fx.fill({ color: 0xe1cfaf, alpha: 0.28 * (1 - phase) });
      }
    }

    // People walking the road, gate to Main Building and back.
    const path = ROAD.map(([x, y]) => gridToScreen(x, y));
    for (const p of this.people) {
      const along = (t * p.speed + p.offset) % 2;
      const s = along < 1 ? along : 2 - along;
      const f = s * (path.length - 1);
      const i = Math.min(path.length - 2, Math.floor(f));
      const k = f - i;
      const a = path[i]!;
      const b = path[i + 1]!;
      const x = a.x + (b.x - a.x) * k;
      const y = a.y + (b.y - a.y) * k;
      const g = p.g;
      g.clear();
      g.ellipse(x, y, 4, 1.8);
      g.fill({ color: 0x000000, alpha: 0.25 });
      const ga = ROAD[i]!;
      const gb = ROAD[i + 1]!;
      const sign = along < 1 ? 1 : -1;
      const direction = facingFor((gb[0] - ga[0]) * sign, (gb[1] - ga[1]) * sign);
      const step = Math.floor((t * p.speed + p.offset) * (path.length - 1) * 10) % 6;
      const texture = this.unitArt?.textures[`worker_${direction}_${step}`];
      if (texture) {
        let sprite = this.walkers.get(g);
        if (!sprite) {
          sprite = new Sprite(texture);
          g.addChild(sprite);
          this.walkers.set(g, sprite);
        }
        sprite.texture = texture;
        sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
        sprite.position.set(x, y);
      } else {
        const bob = Math.abs(Math.sin(t * 9 + p.offset * 10)) * 1.5;
        g.roundRect(x - 2.6, y - 9 - bob, 5.2, 7, 2);
        g.fill({ color: p.tint });
        g.circle(x, y - 11.5 - bob, 2.4);
        g.fill({ color: 0xf1c9a5 });
      }
      const cell = ROAD[Math.round(i + k)] ?? ROAD[i]!;
      g.zIndex = cell[0] + cell[1] + 0.7;
    }

    for (const [id, sprite] of this.builders) {
      const plot = PLOTS.find((p) => p.id === id)!;
      const c = centreOf(plot);
      const work = worksiteMotion(t, plot.x * 0.7);
      const texture =
        this.unitArt?.textures[
          work.working
            ? workerAction(t + plot.x * 0.7, false, this.reducedMotion.matches)
            : `worker_${work.facing}_${work.frame}`
        ] ?? this.unitArt?.textures[`worker_${work.facing}_${work.frame}`];
      if (texture) {
        sprite.texture = texture;
        sprite.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
      }
      sprite.position.set(c.x - 16 + work.along * 24, c.y + 12 + work.along * 12);
      sprite.rotation = 0;
      if (!this.reducedMotion.matches && work.working) {
        fx.circle(sprite.x + 5, sprite.y - 3 - work.strike * 7, 2 + work.strike * 3);
        fx.fill({ color: 0xdfc18b, alpha: work.strike * 0.2 });
      }
    }
    if ((this.state.levels.shrine ?? 0) > 0) {
      const c = centreOf(PLOTS.find((p) => p.id === "shrine")!);
      const pulse = 0.15 + (Math.sin(t * 1.8) + 1) * 0.05;
      fx.ellipse(c.x, c.y - 20, 9, 5);
      fx.fill({ color: 0xffcf67, alpha: pulse });
      for (let k = 0; k < 4; k++) {
        const phase = (t * 0.18 + k / 4) % 1;
        fx.circle(c.x + Math.sin(phase * 7 + k) * 10, c.y - 20 - phase * 24, 1.1);
        fx.fill({ color: 0xffd98c, alpha: 0.5 * (1 - phase) });
      }
    }
    for (const [id, started] of this.celebrations) {
      const age = now - started;
      if (age > 1.8 || this.reducedMotion.matches) {
        this.celebrations.delete(id);
        continue;
      }
      const c = centreOf(PLOTS.find((p) => p.id === id)!);
      const fade = 1 - age / 1.8;
      fx.ellipse(c.x, c.y, 18 + age * 26, 9 + age * 13);
      fx.stroke({ color: 0xf6d087, width: 2, alpha: fade });
      for (let k = 0; k < 8; k++) {
        const angle = (k * Math.PI) / 4;
        fx.circle(
          c.x + Math.cos(angle) * age * 24,
          c.y + Math.sin(angle) * age * 10 - age * 18,
          1.8 * fade
        );
        fx.fill({ color: 0xffe7a9, alpha: fade });
      }
    }

    // A recruit drilling at the Barracks while troops train.
    if (this.recruit) this.recruit.visible = false;
    if (this.state.training && (this.state.levels.barracks ?? 0) > 0) {
      const c = centreOf(PLOTS.find((q) => q.id === "barracks")!);
      const drill = this.reducedMotion.matches ? 0 : t % 6;
      const texture =
        this.unitArt?.textures[
          drill < 2
            ? "militia_SW_ready"
            : `militia_${drill < 4 ? "SW" : "NE"}_${Math.floor(t * 5) % 6}`
        ] ?? this.unitArt?.textures.militia_SW_0;
      if (texture) {
        if (!this.recruit) {
          this.recruit = new Sprite(texture);
          this.recruit.scale.set(0.72);
          this.recruit.zIndex = depthOf(PLOTS.find((q) => q.id === "barracks")!) + 0.8;
          this.objects.addChild(this.recruit);
        }
        this.recruit.texture = texture;
        this.recruit.anchor.set(texture.defaultAnchor?.x ?? 0.5, texture.defaultAnchor?.y ?? 1);
        // Two short march legs and a halt, so walking feet do not skate in place.
        const along = drill < 2 ? 0 : drill < 4 ? (drill - 2) * 3 : (6 - drill) * 3;
        this.recruit.position.set(c.x + 26 - along, c.y + 10 + along * 0.5);
        this.recruit.visible = true;
      } else {
        const step = Math.floor(t * 2) % 2;
        fx.roundRect(c.x + 26 + step * 2, c.y + 2, 5, 7, 2);
        fx.fill({ color: 0xb1462f });
        fx.circle(c.x + 28.5 + step * 2, c.y - 0.5, 2.3);
        fx.fill({ color: 0xf1c9a5 });
      }
    }

    // The next-step arrow: under the building's name tag, pointing up at it.
    // Over the roof it was ambiguous — roofs overlap, tags do not.
    const arrow = this.hintArrow;
    arrow.clear();
    const tag = this.state.hint ? this.tags.get(this.state.hint) : undefined;
    if (tag) {
      const k = 1 / this.world.scale.x;
      const x = tag.x;
      const tip = tag.y + (22 + Math.abs(Math.sin(t * 4)) * 6) * k;
      arrow.poly([x, tip, x + 11 * k, tip + 16 * k, x - 11 * k, tip + 16 * k]);
      arrow.fill({ color: hex(palette.gold) });
      arrow.stroke({ color: hex(palette.bg), width: 2 * k });
    }
  }

  // --- input and view ------------------------------------------------------

  private bindPointer(): void {
    const stage = this.app.stage;
    stage.eventMode = "static";
    stage.hitArea = { contains: () => true };

    stage.on("pointerdown", (e: FederatedPointerEvent) => {
      this.pointers.set(e.pointerId, { x: e.global.x, y: e.global.y });
      if (this.pointers.size === 1) {
        this.pressedAt = { x: e.global.x, y: e.global.y };
        this.dragged = false;
      } else {
        this.gesture = this.readGesture();
        this.dragged = true;
      }
    });

    stage.on("pointermove", (e: FederatedPointerEvent) => {
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      const now = { x: e.global.x, y: e.global.y };
      this.pointers.set(e.pointerId, now);
      if (this.pointers.size > 1) {
        const next = this.readGesture();
        if (this.gesture && next && this.gesture.distance > 0) {
          this.zoomAround(next.midpoint, next.distance / this.gesture.distance);
          this.panBy(
            next.midpoint.x - this.gesture.midpoint.x,
            next.midpoint.y - this.gesture.midpoint.y
          );
        }
        this.gesture = next;
        return;
      }
      if (
        this.pressedAt &&
        Math.hypot(now.x - this.pressedAt.x, now.y - this.pressedAt.y) > TAP_SLOP
      ) {
        this.dragged = true;
      }
      // One finger drags the village around: there is nothing to paint here.
      if (this.dragged) this.panBy(now.x - prev.x, now.y - prev.y);
    });

    const end = (e: FederatedPointerEvent): void => {
      this.pointers.delete(e.pointerId);
      this.gesture = this.pointers.size > 1 ? this.readGesture() : null;
      if (this.pointers.size > 0) return;
      if (this.pressedAt && !this.dragged) {
        const id = this.buildingAt({ x: e.global.x, y: e.global.y });
        if (id) this.onTapFn?.(id);
      }
      this.pressedAt = null;
    };
    stage.on("pointerup", end);
    stage.on("pointerupoutside", end);

    this.app.canvas.addEventListener(
      "wheel",
      (event: WheelEvent) => {
        event.preventDefault();
        const rect = this.app.canvas.getBoundingClientRect();
        this.zoomAround(
          { x: event.clientX - rect.left, y: event.clientY - rect.top },
          event.deltaY < 0 ? 1.12 : 1 / 1.12
        );
      },
      { passive: false }
    );
  }

  /**
   * The building under a canvas point: the front-most picture whose bounds
   * hold it, else the plot under it. Pictures first, because a tall building's
   * roof stands over the plots behind it, and a tap on the roof means the
   * building.
   */
  buildingAt(global: Point): string | null {
    const local = this.world.toLocal(global);
    // 1. The ground a building stands on: exact, and never ambiguous.
    const gx = Math.round(local.x / 64 + local.y / 32);
    const gy = Math.round(local.y / 32 - local.x / 64);
    for (const plot of PLOTS) {
      if (gx >= plot.x && gx < plot.x + plot.w && gy >= plot.y && gy < plot.y + plot.d)
        return plot.id;
    }
    // 2. The name tags: what the eye reads, so what gets tapped.
    for (const [id, tag] of this.tags) {
      const b = tag.getBounds();
      if (
        global.x >= b.minX &&
        global.x <= b.maxX &&
        global.y >= b.minY - 2 &&
        global.y <= b.maxY + 2
      )
        return id;
    }
    // 3. The pictures, front-most first (a roof stands over the plots behind).
    const byDepth = [...PLOTS].sort((a, b) => depthOf(b) - depthOf(a));
    for (const plot of byDepth) {
      const sprite = this.plotSprites.get(plot.id)?.children[0] as Sprite | undefined;
      if (!sprite) continue;
      const b = sprite.getLocalBounds();
      const w = b.maxX - b.minX;
      const h = b.maxY - b.minY;
      const x0 = sprite.x + b.minX + w * 0.12;
      const x1 = sprite.x + b.maxX - w * 0.12;
      const y0 = sprite.y + b.minY + h * 0.05;
      const y1 = sprite.y + b.maxY;
      if (local.x >= x0 && local.x <= x1 && local.y >= y0 && local.y <= y1) return plot.id;
    }
    return null;
  }

  private readGesture(): { distance: number; midpoint: Point } | null {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return null;
    return {
      distance: Math.hypot(a.x - b.x, a.y - b.y),
      midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
    };
  }

  /** An extent in world pixels, with room above for roofs and trees. */
  private extentOf(cells: [number, number][]): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    for (const [x, y] of cells) {
      const c = gridToScreen(x, y);
      minX = Math.min(minX, c.x - 32);
      maxX = Math.max(maxX, c.x + 32);
      minY = Math.min(minY, c.y - 16 - 90);
      maxY = Math.max(maxY, c.y + 24);
    }
    return { minX, maxX, minY, maxY };
  }

  /** The clearing: what "fit" shows. */
  private extent(): { minX: number; maxX: number; minY: number; maxY: number } {
    return this.extentOf(villageCells());
  }

  private fitScale(): number {
    const e = this.extent();
    return Math.min(
      (this.app.screen.width * 0.96) / (e.maxX - e.minX),
      (this.app.screen.height * 0.96) / (e.maxY - e.minY)
    );
  }

  /** The whole round village on screen, centred. */
  frame(): void {
    const scale = this.fitScale();
    this.world.scale.set(scale);
    const e = this.extent();
    this.world.x = this.app.screen.width / 2 - ((e.minX + e.maxX) / 2) * scale;
    this.world.y = this.app.screen.height / 2 - ((e.minY + e.maxY) / 2) * scale;
    this.clamp();
    this.fitMarks();
  }

  private zoomAround(screen: Point, factor: number): void {
    const before = this.world.scale.x;
    const after = Math.max(this.fitScale() * 0.9, Math.min(MAX_SCALE, before * factor));
    if (after === before) return;
    const ratio = after / before;
    this.world.scale.set(after);
    this.world.x = screen.x - (screen.x - this.world.x) * ratio;
    this.world.y = screen.y - (screen.y - this.world.y) * ratio;
    this.clamp();
    this.fitMarks();
  }

  private panBy(dx: number, dy: number): void {
    this.world.x += dx;
    this.world.y += dy;
    this.clamp();
  }

  /** Keep some of the village on screen, whatever the finger does. */
  private clamp(): void {
    const e = this.extent();
    const s = this.world.scale.x;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const margin = 60;
    this.world.x = Math.min(w - margin - e.minX * s, Math.max(margin - e.maxX * s, this.world.x));
    this.world.y = Math.min(h - margin - e.minY * s, Math.max(margin - e.maxY * s, this.world.y));
    this.backdrop?.sync(this.app, this.world);
  }
}
